import { ORDER_TRACKING_BACKFILL_DAYS } from "@redotech/order-tracking-common/constants";
import { OTV2_DEFAULT_MAX_ORDER_AGE_DAYS } from "@redotech/order-tracking-common/order-tracking-types";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { TeamContext } from "../../team";
import { IntegrationEventTestCard } from "./integration-event-test-card";
import { KlaviyoMetricsHealthCheckCard } from "./klaviyo-metrics-health-check";
import { MalomoMigrationCard } from "./malomo-migration-card";
import { ScanKlaviyoTemplatesCard } from "./scan-klaviyo-templates-for-malomo-links";

const POLL_FREQUENCY_MS = 3000;

export const orderTrackingV2Form = groupInput({
  enabled: input<boolean>(),
  setTrackingUrl: input<boolean>(),
  setTrackingUrlOnSource: input<boolean>(),
  dispatchEnabled: input<boolean>(),
  defaultTrackingPageUrl: input<string>({}),
  backfillDays: input<string>({
    validator: numberValidator({
      min: 1,
      max: OTV2_DEFAULT_MAX_ORDER_AGE_DAYS,
    }),
  }),
});

export type OrderTrackingV2Form = InputSchema.Input<typeof orderTrackingV2Form>;

export type OrderTrackingV2Value = InputSchema.Value<
  typeof orderTrackingV2Form
>;

export const orderTrackingV2Default: OrderTrackingV2Value = {
  enabled: false,
  setTrackingUrl: false,
  setTrackingUrlOnSource: false,
  dispatchEnabled: false,
  defaultTrackingPageUrl: "",
  backfillDays: String(ORDER_TRACKING_BACKFILL_DAYS),
};

export const OrderTrackingV2Card = memo(function OrderTrackingV2Card({
  input,
  perShipmentBillingEnabled,
}: {
  input: OrderTrackingV2Form;
  perShipmentBillingEnabled: boolean;
}) {
  const isAuthorized = useHasMarketingPermission("marketing_admin");
  const team = useContext(TeamContext);
  const rpcClient = useContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();

  const { data: progressData } = useQuery({
    queryKey: ["orderTrackingBackfillProgress", team?._id],
    queryFn: () => {
      if (!team || !rpcClient) {
        throw new Error("Team or RPC client not available");
      }
      return rpcClient.getOrderTrackingBackfillProgress({ teamId: team._id });
    },
    enabled: !!team && !!rpcClient,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.found && data?.running ? POLL_FREQUENCY_MS : false;
    },
    refetchOnWindowFocus: false,
  });

  const isRunning = progressData?.found && progressData?.running;
  const isCompleted =
    progressData?.found && !progressData?.running && progressData?.progress;
  const progress = isRunning ? progressData.progress : null;
  const finalStats = isCompleted ? progressData.progress : null;

  const triggerBackfillMutation = useMutation({
    mutationFn: async () => {
      if (!team || !rpcClient) {
        throw new Error("Team or RPC client not available");
      }
      const days =
        parseInt(input.inputs.backfillDays.value) ||
        ORDER_TRACKING_BACKFILL_DAYS;
      return rpcClient.triggerOrderTrackingBackfill({ teamId: team._id, days });
    },
    onSuccess: (response) => {
      if (response.success) {
        toast(
          `Backfill started successfully. Workflow ID: ${response.workflowId}`,
          { variant: "success" },
        );
        void queryClient.invalidateQueries({
          queryKey: ["orderTrackingBackfillProgress", team?._id],
        });
      } else {
        toast(response.error || "Unknown error occurred", { variant: "error" });
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to start backfill: ${errorMessage}`, { variant: "error" });
    },
  });

  return (
    <Card title="Order Tracking V2">
      <Flex dir="column" gap="lg">
        <section className={gridCss.grid}>
          <div className={gridCss.span6L}>
            <FormSwitch
              disabled={!isAuthorized || perShipmentBillingEnabled}
              disabledTooltip={
                perShipmentBillingEnabled
                  ? "Cannot disable Order Tracking V2 if per-shipment billing is enabled"
                  : undefined
              }
              input={input.inputs.enabled}
              label="V2 Enabled"
            >
              Enable order tracking V2 workflow processing
            </FormSwitch>
          </div>
          <div className={gridCss.span6L}>
            <FormSwitch
              disabled={!isAuthorized}
              input={input.inputs.dispatchEnabled}
              label="Dispatch Enabled"
            >
              When enabled, V2 workflows will dispatch events to integrations
              (Klaviyo, Attentive, Postscript)
            </FormSwitch>
          </div>
          <div className={gridCss.span6L}>
            <FormSwitch
              disabled={!isAuthorized}
              input={input.inputs.setTrackingUrl}
              label="Set Tracking URL"
            >
              Store the tracking page URL internally in Redo for each order and
              update the URL in shipping confirmation/update emails
            </FormSwitch>
          </div>
          <div className={gridCss.span6L}>
            <FormSwitch
              disabled={!isAuthorized || !input.inputs.setTrackingUrl.value}
              input={input.inputs.setTrackingUrlOnSource}
              label="Set Tracking URL on Source"
            >
              Update the tracking page URL on Shopify fulfillments. Requires
              &quot;Set Tracking URL&quot; to be enabled.
            </FormSwitch>
          </div>
          <div className={gridCss.span12}>
            <Flex dir="column" gap="sm">
              <FormTextInput
                input={input.inputs.defaultTrackingPageUrl}
                label="Default Tracking Page URL"
                placeholder="https://example.com/tracking"
                type="url"
              />
              <Text fontSize="xs">
                URL used for embedded tracking pages (Order Tracking v2 only)
              </Text>
            </Flex>
          </div>
        </section>
        <hr
          style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: 0 }}
        />
        <Flex dir="column" gap="sm">
          <Text fontSize="md" fontWeight="semibold">
            Order Tracking Backfill
          </Text>
          <Flex align="flex-end" gap="md">
            <FormTextInput
              input={input.inputs.backfillDays}
              label={`Days to backfill (max ${OTV2_DEFAULT_MAX_ORDER_AGE_DAYS})`}
              max={OTV2_DEFAULT_MAX_ORDER_AGE_DAYS}
              min={1}
              placeholder={String(ORDER_TRACKING_BACKFILL_DAYS)}
              type="number"
            />
            <RedoButton
              disabled={
                isRunning ||
                triggerBackfillMutation.isPending ||
                !team ||
                !rpcClient
              }
              hierarchy="primary"
              onClick={() => triggerBackfillMutation.mutate()}
              size="sm"
              text={
                isRunning
                  ? "Running..."
                  : triggerBackfillMutation.isPending
                    ? "Starting..."
                    : "Start backfill"
              }
            />
          </Flex>
          <Text fontSize="xs">
            Backfill existing orders into V2 workflows. Notifications suppressed
            for 2 hours.
          </Text>
          {isRunning && progress && (
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium">
                Backfill in progress: {progress.progressPercent}
              </Text>
              <Text fontSize="sm">
                {progress.totalProcessed} / {progress.totalCount} processed (
                {progress.workflowsStarted} started, {progress.skippedNoShopify}{" "}
                skipped)
              </Text>
              {progress.errors > 0 && (
                <Text fontSize="sm" textColor="error">
                  Errors: {progress.errors}
                </Text>
              )}
            </Flex>
          )}
          {isCompleted && finalStats && (
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" fontWeight="medium" textColor="success">
                Backfill completed
              </Text>
              <Text fontSize="sm">
                {finalStats.totalProcessed} / {finalStats.totalCount} processed
                ({finalStats.workflowsStarted} started,{" "}
                {finalStats.skippedNoShopify} skipped)
              </Text>
              {finalStats.errors > 0 && (
                <Text fontSize="sm" textColor="error">
                  Errors: {finalStats.errors}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
        <hr
          style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: 0 }}
        />
        <IntegrationEventTestCard />
        <MalomoMigrationCard />
        <ScanKlaviyoTemplatesCard />
        <KlaviyoMetricsHealthCheckCard />
      </Flex>
    </Card>
  );
});
